import React, { useState } from "react";
import toast from "react-hot-toast";

import Modal from "../../../components/modal/Modal";
import Input from "../../../components/form-item/Input";
import Textarea from "../../../components/form-item/Textarea";
import type {
  AiScene,
  AiScenePreset,
  AiScenePresetInput,
} from "../../../app/services/adminAi";

export interface ScenePresetModalProps {
  scene: AiScene;
  /** 編集対象。未指定なら新規作成。 */
  initial?: AiScenePreset | null;
  isSaving: boolean;
  onSubmit: (body: AiScenePresetInput) => void;
  onClose: () => void;
}

/**
 * シーン別プロンプトプリセットの作成 / 編集モーダル。
 *
 * システムプロンプト / ルール / 履歴件数を編集する。空欄のフィールドは
 * 実行時にコード上のデフォルト文面へフォールバックするため、プレース
 * ホルダーとして現在のデフォルトを表示する。
 *
 * 画像生成シーン（kind === "image"）では履歴を使わないため、履歴件数の
 * 入力欄は表示しない（システムプロンプト = 基本指示 / ルール = スタイル制約）。
 */
const ScenePresetModal: React.FC<ScenePresetModalProps> = ({
  scene,
  initial,
  isSaving,
  onSubmit,
  onClose,
}) => {
  const isEdit = Boolean(initial);
  const isImageScene = scene.kind === "image";

  const [name, setName] = useState(initial?.name ?? "");
  const [systemPrompt, setSystemPrompt] = useState(
    initial?.system_prompt ?? "",
  );
  const [rules, setRules] = useState(initial?.rules ?? "");
  const [historyLimit, setHistoryLimit] = useState(
    String(initial?.history_limit ?? scene.default_history_limit),
  );

  const handleSubmit = () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("プリセット名を入力してください");
      return;
    }
    if (isImageScene) {
      onSubmit({
        name: trimmedName,
        system_prompt: systemPrompt,
        rules,
      });
      return;
    }
    const limitNum = Number(historyLimit);
    if (!Number.isInteger(limitNum) || limitNum < 0) {
      toast.error("履歴件数には 0 以上の整数を入力してください");
      return;
    }
    onSubmit({
      name: trimmedName,
      system_prompt: systemPrompt,
      rules,
      history_limit: limitNum,
    });
  };

  return (
    <Modal
      title={isEdit ? "プロンプト案を編集" : "プロンプト案を作成"}
      description={scene.display_name}
      size="default"
      onClose={onClose}
      primaryAction={{
        label: isEdit ? "保存" : "作成",
        onClick: handleSubmit,
        loading: isSaving,
        disabled: isSaving,
      }}
      secondaryAction={{
        label: "キャンセル",
        onClick: onClose,
      }}
    >
      <div className="u-col u-w100 u-gap16">
        <Input
          label="プリセット名"
          option="required"
          placeholder="例: 案A"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Textarea
          label={isImageScene ? "システムプロンプト（基本指示）" : "システムプロンプト"}
          option="optional"
          rows={8}
          placeholder={`空ならデフォルトを使用:\n${scene.default_system_prompt}`}
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
        />
        <Textarea
          label={isImageScene ? "ルール（スタイル・構図の制約）" : "ルール"}
          option="optional"
          rows={8}
          placeholder={`空ならデフォルトを使用:\n${scene.default_rules}`}
          value={rules}
          onChange={(e) => setRules(e.target.value)}
        />
        {!isImageScene && (
          <Input
            label="プロンプトに含める履歴の最大件数"
            type="number"
            width="240px"
            value={historyLimit}
            onChange={(e) => setHistoryLimit(e.target.value)}
          />
        )}
      </div>
    </Modal>
  );
};

export default ScenePresetModal;
